import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import useTranslations from '@/i18n/useTranslations';

import { INSTRUMENTS } from '../audio/instruments';
import {
  BEATS_PER_BAR,
  LOOP_LENGTH_BEATS,
  MAX_EVENTS_PER_LAYER,
  MAX_LAYERS,
  SCALE_LANES,
  SCALES,
  VIEWBOX_HEIGHT,
  VIEWBOX_WIDTH,
} from '../constants';
import {
  buildPath,
  clamp,
  createEmptyLayer,
  getLaneFromPoint,
  midiToLabel,
  normalizeVelocity,
  quantizeBeat,
  toFrequencyFromLane,
} from '../utils';

const SAVE_LIMIT = 6;

const TIME_THRESHOLD_S = 0.28;          // min time between non-lane-change triggers
const DISTANCE_THRESHOLD_PX = 22;       // min movement to allow non-lane-change triggers
const LANE_SMOOTHING_ALPHA = 0.18;      // low-pass for x used in lane detection (hysteresis)
const ECHO_SHORT_TAIL_S = 0.32;         // how long notes linger when echo is off

const useLatestRef = (value) => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
};

const BACKGROUND_STOPS = [
  { offset: 0, color: 'rgba(14, 19, 29, 0.94)' },
  { offset: 0.5, color: 'rgba(16, 32, 55, 0.96)' },
  { offset: 1, color: 'rgba(14, 19, 29, 0.94)' },
];

const LANE_LINE_COLOR = 'rgba(108, 134, 255, 0.12)';
const LANE_LINE_WIDTH = 1.6;
const LANE_LINE_DASH = [4, 14];
const LANE_DOT_COLOR = 'rgba(118, 152, 255, 0.28)';
const LANE_DOT_RADIUS = 6;
const LANE_DOT_PADDING = 36;

const STROKE_WIDTH = 12;
const STROKE_ALPHA = 0.9;
const LIVE_STROKE_ALPHA = 0.7;
const LIVE_STROKE_DASH = [6, 18];
const STROKE_SHADOW_BLUR = 18;

const getBrushColor = (brushId, fallbackBrush) => {
  const brushEntry = INSTRUMENTS.find((entry) => entry.id === brushId);
  if (brushEntry?.color) {
    return brushEntry.color;
  }
  return fallbackBrush?.color ?? '#ffffff';
};

const renderChromaticComposition = ({
  context,
  layers,
  liveStroke,
  fallbackBrush,
}) => {
  const { canvas } = context;
  const { width, height } = canvas;

  context.save();
  context.clearRect(0, 0, width, height);

  const gradient = context.createLinearGradient(0, 0, width, 0);
  BACKGROUND_STOPS.forEach((stop) => {
    gradient.addColorStop(stop.offset, stop.color);
  });
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);

  context.save();
  context.strokeStyle = LANE_LINE_COLOR;
  context.lineWidth = LANE_LINE_WIDTH;
  context.setLineDash(LANE_LINE_DASH);
  const laneCount = SCALE_LANES || 1;
  const laneWidth = width / laneCount;
  for (let lane = 0; lane <= laneCount; lane += 1) {
    const x = laneWidth * lane;
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, height);
    context.stroke();
  }
  context.restore();

  context.save();
  context.fillStyle = LANE_DOT_COLOR;
  const dotYTop = LANE_DOT_PADDING;
  const dotYBottom = height - LANE_DOT_PADDING;
  for (let lane = 0; lane < laneCount; lane += 1) {
    const cx = laneWidth * lane + laneWidth / 2;
    context.beginPath();
    context.arc(cx, dotYTop, LANE_DOT_RADIUS, 0, Math.PI * 2);
    context.fill();
    context.beginPath();
    context.arc(cx, dotYBottom, LANE_DOT_RADIUS, 0, Math.PI * 2);
    context.fill();
  }
  context.restore();

  const strokes = [];
  const layerList = Array.isArray(layers) ? layers : [];
  layerList.forEach((layer) => {
    layer.strokes.forEach((stroke) => {
      if (Array.isArray(stroke.points) && stroke.points.length) {
        strokes.push({ ...stroke, isLive: false });
      }
    });
  });
  if (liveStroke?.points?.length) {
    strokes.push({ ...liveStroke, isLive: true });
  }

  if (!strokes.length) {
    context.restore();
    return;
  }

  context.save();
  context.lineWidth = STROKE_WIDTH;
  context.lineJoin = 'round';
  context.lineCap = 'round';
  context.shadowBlur = STROKE_SHADOW_BLUR;
  context.globalCompositeOperation = 'screen';
  if (context.globalCompositeOperation !== 'screen') {
    context.globalCompositeOperation = 'lighter';
  }

  strokes.forEach((stroke) => {
    const color = getBrushColor(stroke.brushId, fallbackBrush);
    if (!color) {
      return;
    }

    const scaledPoints = stroke.points.map((point) => ({
      x: point.x * width,
      y: point.y * height,
    }));

    if (!scaledPoints.length) {
      return;
    }

    context.save();
    context.strokeStyle = color;
    context.shadowColor = color;
    context.globalAlpha = stroke.isLive ? LIVE_STROKE_ALPHA : STROKE_ALPHA;
    context.setLineDash(stroke.isLive ? LIVE_STROKE_DASH : []);

    context.beginPath();
    context.moveTo(scaledPoints[0].x, scaledPoints[0].y);

    if (scaledPoints.length === 1) {
      context.lineTo(scaledPoints[0].x + LANE_DOT_RADIUS, scaledPoints[0].y + LANE_DOT_RADIUS);
    } else {
      for (let index = 1; index < scaledPoints.length; index += 1) {
        const current = scaledPoints[index];
        const previous = scaledPoints[index - 1];
        const midX = (previous.x + current.x) / 2;
        const midY = (previous.y + current.y) / 2;
        context.quadraticCurveTo(previous.x, previous.y, midX, midY);
      }
      const last = scaledPoints[scaledPoints.length - 1];
      context.lineTo(last.x, last.y);
    }

    context.stroke();
    context.restore();
  });

  context.restore();
  context.restore();
};

export const useChromaticMusicEngine = () => {
  const t = useTranslations('common.newspaper.art_games.chromatic_music.game');
  const svgRef = useRef(null);
  const audioContextRef = useRef(null);
  const masterGainRef = useRef(null);
  const compressorRef = useRef(null);
  const delayRef = useRef(null);
  const delaySendRef = useRef(null);   // input (dry -> delay)
  const delayOutRef = useRef(null);    // output (delay -> mix)  
  const delayFeedbackRef = useRef(null);
  const layerGainsRef = useRef(new Map());
  const loopStartRef = useRef(null);
  const pendingEventsRef = useRef([]);
  const pointerStateRef = useRef({ drawing: false });
  const timeoutsRef = useRef([]);
  const activeVoicesRef = useRef(new Set());
  const mediaDestRef = useRef(null);   // NEW: audio stream out for recording

  const [brushId, setBrushId] = useState(INSTRUMENTS[0].id);
  const [scaleId, setScaleId] = useState(SCALES[0].id);
  const [tempo, setTempo] = useState(100);
  const [loopEnabled, setLoopEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackMode, setPlaybackMode] = useState('gesture'); // 'gesture' | 'scan-lr' | 'scan-rl'
  const [echoEnabled, setEchoEnabled] = useState(false);
  const [quietMode, setQuietMode] = useState(false);
  const [masterVolume, setMasterVolume] = useState(0.85);
  const [layers, setLayers] = useState([createEmptyLayer(0)]);
  const [activeLayerId, setActiveLayerId] = useState('layer-1');
  const [liveStroke, setLiveStroke] = useState(null);
  const [recentNotes, setRecentNotes] = useState([]);
  const [metronomeBeat, setMetronomeBeat] = useState(0);
  const [metronomePhase, setMetronomePhase] = useState(0);
  const [layerError, setLayerError] = useState('');
  const [canvasActive, setCanvasActive] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [savedPieces, setSavedPieces] = useState([]);
  const [isSavingPiece, setIsSavingPiece] = useState(false);
  const [saveToast, setSaveToast] = useState(null);

  const brush = useMemo(
    () => INSTRUMENTS.find((entry) => entry.id === brushId) ?? INSTRUMENTS[0],
    [brushId],
  );

  const scale = useMemo(
    () => SCALES.find((entry) => entry.id === scaleId) ?? SCALES[0],
    [scaleId],
  );

  const activeLayer = useMemo(
    () => layers.find((layer) => layer.id === activeLayerId) ?? layers[0],
    [layers, activeLayerId],
  );

  const layersRef = useLatestRef(layers);
  const tempoRef = useLatestRef(tempo);
  const scaleRef = useLatestRef(scale);
  const loopEnabledRef = useLatestRef(loopEnabled);
  const isPlayingRef = useLatestRef(isPlaying);
  const playbackModeRef = useLatestRef(playbackMode);
  const masterVolumeRef = useLatestRef(masterVolume);
  const quietModeRef = useLatestRef(quietMode);
  const echoEnabledRef = useLatestRef(echoEnabled);
  const savedPiecesRef = useLatestRef(savedPieces);
  const isSavingRef = useLatestRef(isSavingPiece);
  const brushRef = useLatestRef(brush);
  const liveStrokeRef = useLatestRef(liveStroke);

  const saveToastTimeoutRef = useRef(null);
  const previousAudioUrlsRef = useRef([]);
  const previousImageUrlsRef = useRef([]);

  const showSaveToast = useCallback((message) => {
    window.clearTimeout(saveToastTimeoutRef.current);
    setSaveToast(message);

    if (message) {
      saveToastTimeoutRef.current = window.setTimeout(() => {
        setSaveToast(null);
      }, 2600);
    }
  }, []);

  const ensureAudio = useCallback(() => {
    if (audioContextRef.current) {
      return audioContextRef.current;
    }

    const context = new (window.AudioContext || window.webkitAudioContext)();
    const masterGain = context.createGain();
    masterGain.gain.value = quietModeRef.current ? 0.2 : masterVolumeRef.current;

    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -16;
    compressor.knee.value = 18;
    compressor.ratio.value = 3;
    compressor.attack.value = 0.012;
    compressor.release.value = 0.25;

    // --- Master tone shaper (subtle, musical) ---
    const lowShelf = context.createBiquadFilter();
    lowShelf.type = 'lowshelf';
    lowShelf.frequency.value = 160;     // warm voice without rumble
    lowShelf.gain.value = -1.5;         // -1.5 dB

    const highShelf = context.createBiquadFilter();
    highShelf.type = 'highshelf';
    highShelf.frequency.value = 4200;   // add air, avoid harshness
    highShelf.gain.value = +1.8;        // +1.8 dB

    // NEW: a dedicated dry mix gain so we can duck the dry when echo is ON
    const dryMix = context.createGain();
    dryMix.gain.value = 1.0;

    const delay = context.createDelay(2.0); // max delay ~2s is safe
    // Tempo-sync: dotted eighth = 0.75 * secondsPerBeat
    delay.delayTime.value = (60 / tempoRef.current) * 0.75;
    const delaySend = context.createGain();     // dry -> delay
    delaySend.gain.value = 0;                   // start muted
    const delayFeedback = context.createGain(); // feedback in delay loop
    delayFeedback.gain.value = 0;               // start muted
    const delayOut = context.createGain();      // delay -> wet mix
    delayOut.gain.value = 0;                    // start muted

    masterGain.connect(lowShelf);       // dry path -> tone -> dryMix -> comp
    lowShelf.connect(highShelf);
    highShelf.connect(dryMix);
    dryMix.connect(compressor);

    masterGain.connect(delaySend);      // wet path (gated in)
    delaySend.connect(delay);
    delay.connect(delayFeedback);
    delayFeedback.connect(delay);       // feedback loop
    delay.connect(delayOut);            // gated out
    delayOut.connect(compressor);       // wet to mix (after tone shaper)

    // --- Recording tap (audio) ---
    const mediaDest = context.createMediaStreamDestination();
    compressor.connect(mediaDest);      // send post-mix audio to recorder
    compressor.connect(context.destination);

    audioContextRef.current = context;
    masterGainRef.current = masterGain;
    compressorRef.current = compressor;
    delayRef.current = delay;
    delaySendRef.current = delaySend;
    delayFeedbackRef.current = delayFeedback;
    delayOutRef.current = delayOut;
    // store dryMix so we can duck later
    // (reuse an existing ref container to avoid adding new state)
    // @ts-ignore
    audioContextRef.current.__dryMix = dryMix;
    mediaDestRef.current = mediaDest;

    setAudioReady(true);

    return context;
  }, [masterVolumeRef, quietModeRef, tempoRef]);

  const updateMasterVolume = useCallback(() => {
    if (!masterGainRef.current || !audioContextRef.current) {
      return;
    }

    const gain = quietModeRef.current
      ? Math.min(masterVolumeRef.current, 0.28)
      : masterVolumeRef.current;
    masterGainRef.current.gain.linearRampToValueAtTime(
      gain,
      audioContextRef.current.currentTime + 0.05,
    );
  }, [masterVolumeRef, quietModeRef]);

  useEffect(() => {
    updateMasterVolume();
  }, [masterVolume, quietMode, updateMasterVolume]);

  useEffect(
    () => () => {
      window.clearTimeout(saveToastTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    const previous = previousAudioUrlsRef.current;
    const nextUrls = savedPieces.map((piece) => piece.audioUrl).filter(Boolean);

    previous.forEach((url) => {
      if (url && !nextUrls.includes(url) && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });

    previousAudioUrlsRef.current = nextUrls;
  }, [savedPieces]);

  useEffect(() => {
    const previous = previousImageUrlsRef.current;
    const nextUrls = savedPieces.map((piece) => piece.imageUrl).filter(Boolean);

    previous.forEach((url) => {
      if (url && !nextUrls.includes(url) && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });

    previousImageUrlsRef.current = nextUrls;
  }, [savedPieces]);

  // Echo mix: loud, obvious repeats when ON, instant kill when OFF, and a small dry duck.
  useEffect(() => {
    if (
      !audioContextRef.current ||
      !delaySendRef.current ||
      !delayFeedbackRef.current ||
      !delayOutRef.current
    ) return;
    const ctx = audioContextRef.current;
    const t = ctx.currentTime;
    const ON = echoEnabled;
    const dryMix = ctx.__dryMix; // from ensureAudio()

    // cancel any ramps first
    delaySendRef.current.gain.cancelScheduledValues(t);
    delayFeedbackRef.current.gain.cancelScheduledValues(t);
    delayOutRef.current.gain.cancelScheduledValues(t);
    if (dryMix) dryMix.gain.cancelScheduledValues(t);

    if (ON) {
      // Wet (clearly audible)
      delaySendRef.current.gain.setValueAtTime(delaySendRef.current.gain.value, t);
      delaySendRef.current.gain.linearRampToValueAtTime(0.55, t + 0.04);
      delayFeedbackRef.current.gain.setValueAtTime(delayFeedbackRef.current.gain.value, t);
      delayFeedbackRef.current.gain.linearRampToValueAtTime(0.34, t + 0.06);
      delayOutRef.current.gain.setValueAtTime(delayOutRef.current.gain.value, t);
      delayOutRef.current.gain.linearRampToValueAtTime(0.55, t + 0.04);

      // Duck dry slightly (-3 dB ≈ 0.707) so repeats pop
      if (dryMix) {
        const target = 0.7;
        dryMix.gain.setValueAtTime(dryMix.gain.value, t);
        dryMix.gain.linearRampToValueAtTime(target, t + 0.04);
      }
    } else {
      // Hard-kill wet, restore dry
      delaySendRef.current.gain.setValueAtTime(0, t);
      delayFeedbackRef.current.gain.setValueAtTime(0, t);
      delayOutRef.current.gain.setValueAtTime(0, t);
      if (dryMix) dryMix.gain.setValueAtTime(1.0, t);
    }
  }, [echoEnabled]);

  useEffect(() => {
    if (echoEnabled) {
      return;
    }
    if (!audioContextRef.current) {
      return;
    }
    const now = audioContextRef.current.currentTime;
    activeVoicesRef.current.forEach((voice) => {
      const gateNode = voice?.gate;
      if (!gateNode || voice.released) {
        return;
      }
      try {
        gateNode.gain.cancelScheduledValues(now);
        gateNode.gain.setValueAtTime(gateNode.gain.value, now);
        gateNode.gain.setTargetAtTime(0.0001, now, 0.05);
      } catch (error) {
        return;
      }

      if (voice.cleanupTimeout) {
        window.clearTimeout(voice.cleanupTimeout);
      }

      const killTimeout = window.setTimeout(() => {
        try {
          gateNode.disconnect();
        } catch (disconnectError) {
          // already disconnected
        }
        activeVoicesRef.current.delete(voice);
      }, 180);

      voice.cleanupTimeout = killTimeout;
      voice.released = true;
      timeoutsRef.current.push(killTimeout);
    });
  }, [echoEnabled]);

  // Keep echo time locked to BPM (grooves at any tempo)
  useEffect(() => {
    if (!audioContextRef.current || !delayRef.current) return;
    const t = audioContextRef.current.currentTime;
    const target = (60 / tempo) * 0.75;
    delayRef.current.delayTime.cancelScheduledValues(t);
    delayRef.current.delayTime.setValueAtTime(delayRef.current.delayTime.value, t);
    delayRef.current.delayTime.linearRampToValueAtTime(target, t + 0.05);
  }, [tempo]);

  const getLayerGain = useCallback(
    (layerId) => {
      const context = ensureAudio();
      if (layerGainsRef.current.has(layerId)) {
        return layerGainsRef.current.get(layerId);
      }

      const gainNode = context.createGain();
      gainNode.gain.value = 1;
      gainNode.connect(masterGainRef.current);
      layerGainsRef.current.set(layerId, gainNode);
      return gainNode;
    },
    [ensureAudio],
  );

  const applyLayerGains = useCallback(() => {
    const audibleLayers = (() => {
      const soloed = layersRef.current.filter((layer) => layer.solo);
      if (soloed.length) {
        return new Set(soloed.map((layer) => layer.id));
      }
      return new Set(layersRef.current.filter((layer) => !layer.muted).map((layer) => layer.id));
    })();

    layersRef.current.forEach((layer) => {
      const gainNode = getLayerGain(layer.id);
      const targetGain = audibleLayers.has(layer.id) ? 1 : 0;
      if (audioContextRef.current) {
        gainNode.gain.cancelScheduledValues(audioContextRef.current.currentTime);
        gainNode.gain.setTargetAtTime(targetGain, audioContextRef.current.currentTime, 0.06);
      } else {
        gainNode.gain.value = targetGain;
      }
    });
  }, [getLayerGain, layersRef]);

  useEffect(() => {
    if (audioContextRef.current) {
      applyLayerGains();
    }
  }, [layers, applyLayerGains]);

  const scheduleNote = useCallback(
    (event, when, layerId) => {
      const context = ensureAudio();
      const gainNode = getLayerGain(layerId);
      const instrument = INSTRUMENTS.find((entry) => entry.id === event.brushId) ?? INSTRUMENTS[0];
      const velocity = normalizeVelocity(event.velocity);
      const voiceGate = context.createGain();
      voiceGate.gain.value = 1;
      voiceGate.connect(gainNode);

      const stopTime = instrument.play({
        context,
        destination: voiceGate,
        frequency: event.frequency,
        velocity,
        when,
        tempo: tempoRef.current,
      });

      const voice = { gate: voiceGate, cleanupTimeout: null, released: false };
      activeVoicesRef.current.add(voice);

      if (!echoEnabledRef.current) {
        const tailTarget = when + ECHO_SHORT_TAIL_S;
        const scheduledStop = typeof stopTime === 'number' ? stopTime : tailTarget;
        const releaseStart = Math.max(when + 0.02, Math.min(tailTarget, scheduledStop));
        voiceGate.gain.setValueAtTime(voiceGate.gain.value, releaseStart);
        voiceGate.gain.setTargetAtTime(0.0001, releaseStart, 0.05);
      }

      const cleanupTime = typeof stopTime === 'number' ? stopTime : when + 4;
      const cleanupDelay = Math.max(0, cleanupTime - context.currentTime + 0.2);
      const timeout = window.setTimeout(() => {
        try {
          voiceGate.disconnect();
        } catch (error) {
          // disconnect may throw if already done; ignore
        }
        activeVoicesRef.current.delete(voice);
      }, cleanupDelay * 1000);
      voice.cleanupTimeout = timeout;
      timeoutsRef.current.push(timeout);
    },
    [activeVoicesRef, echoEnabledRef, ensureAudio, getLayerGain, tempoRef, timeoutsRef],
  );

  const addRecentNote = useCallback((label, color) => {
    const id = uuidv4();
    setRecentNotes((previous) => [...previous.slice(-4), { id, label, color }]);
    const timeout = window.setTimeout(() => {
      setRecentNotes((previous) => previous.filter((entry) => entry.id !== id));
    }, 1200);
    timeoutsRef.current.push(timeout);
  }, []);

  const triggerNote = useCallback(
    (point, velocityHint, strokeId) => {
      const context = ensureAudio();
      const now = context.currentTime;

      if (loopEnabledRef.current && loopStartRef.current === null) {
        loopStartRef.current = now;
      }

      const lane = getLaneFromPoint(point);
      const { midi, frequency } = toFrequencyFromLane(lane, scaleRef.current.steps);

      const yVelocity = 0.35 + (1 - point.y) * 0.65;
      const dynamic = clamp(yVelocity + velocityHint, 0.05, 1);
      const velocity = quietModeRef.current ? dynamic * 0.55 : dynamic;

      // --- Adaptive register velocity: higher = brighter/louder, lower = rounder/softer ---
      const registerBias = 1 + ((midi - 60) / 24) * 0.25; // ±25 % across 2 octaves around middle C
      const adjustedVelocity = clamp(velocity * registerBias, 0.05, 1);

      scheduleNote(
        {
          id: uuidv4(),
          brushId,
          frequency,
          velocity: adjustedVelocity,
        },
        now + 0.004,
        activeLayerId,
      );

      addRecentNote(midiToLabel(midi), brush.color);

      if (!loopEnabledRef.current || loopStartRef.current === null) {
        return;
      }

      let quantized;
      const mode = playbackModeRef.current;
      if (mode === 'scan-lr') {
        // map x ∈ [0..1] across the loop
        quantized = quantizeBeat(point.x * LOOP_LENGTH_BEATS);
      } else if (mode === 'scan-rl') {
        quantized = quantizeBeat((1 - point.x) * LOOP_LENGTH_BEATS);
      } else {
        // gesture-time (current behavior)
        const secondsPerBeat = 60 / tempoRef.current;
        const beatsFromStart = (now - loopStartRef.current) / secondsPerBeat;
        quantized = quantizeBeat(beatsFromStart);
      }

      pendingEventsRef.current.push({
        id: uuidv4(),
        strokeId,
        time: quantized,
        x: point.x,  
        frequency,
        // store what you actually heard during drawing
        velocity: adjustedVelocity,
        brushId,
      });
    },
    [activeLayerId, addRecentNote, brush.color, brushId, ensureAudio, loopEnabledRef, quietModeRef, scheduleNote, scaleRef, tempoRef],
  );

  const handlePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      const svg = svgRef.current;
      if (!svg) {
        return;
      }

      const context = ensureAudio();
      if (context.state === 'suspended') {
        context.resume();
      }

      const pointerId = event.pointerId ?? 'mouse';
      const rect = svg.getBoundingClientRect();
      const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
      const point = { x, y, pressure: event.pressure ?? 0.5, time: context.currentTime };

      pendingEventsRef.current = [];

      const strokeId = uuidv4();
      const initialStroke = {
        id: strokeId,
        brushId,
        points: [point],
        path: buildPath([point]),
      };

      pointerStateRef.current = {
        drawing: true,
        pointerId,
        lastPoint: point,
        lastTriggerTime: context.currentTime,
        lastLane: getLaneFromPoint(point),
        velocity: 0,
        strokeId,
        smoothedX: x,    // seed smoothing with initial x
      };

      setLiveStroke(initialStroke);
      setCanvasActive(true);
      triggerNote(point, 0.15 + point.pressure * 0.2, strokeId);
    },
    [brushId, ensureAudio, triggerNote],
  );

  const handlePointerMove = useCallback(
    (event) => {
      if (!pointerStateRef.current.drawing || event.pointerId !== pointerStateRef.current.pointerId) {
        return;
      }

      const svg = svgRef.current;
      if (!svg) {
        return;
      }

      const rect = svg.getBoundingClientRect();
      const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
      const context = ensureAudio();
      const point = { x, y, pressure: event.pressure ?? 0.4, time: context.currentTime };

      // Smooth X for lane decisions (hysteresis to avoid jitter-flips)
      const prevSmoothedX = pointerStateRef.current.smoothedX ?? x;
      const smoothedX =
        prevSmoothedX * (1 - LANE_SMOOTHING_ALPHA) + x * LANE_SMOOTHING_ALPHA;
      pointerStateRef.current.smoothedX = smoothedX;

      setLiveStroke((previous) => {
        if (!previous) {
          return null;
        }
        const updatedPoints = [...previous.points, point];
        return {
          ...previous,
          points: updatedPoints,
          path: buildPath(updatedPoints),
        };
      });

      const lastPoint = pointerStateRef.current.lastPoint;
      const distance = Math.hypot(
        (point.x - lastPoint.x) * VIEWBOX_WIDTH,
        (point.y - lastPoint.y) * VIEWBOX_HEIGHT,
      );
      const timeDelta = Math.max(point.time - lastPoint.time, 0.0001);
      const speed = distance / timeDelta;
      pointerStateRef.current.velocity = speed;

      const lane = getLaneFromPoint({ ...point, x: smoothedX });
      const laneChanged = lane !== pointerStateRef.current.lastLane;
      const elapsed = point.time - pointerStateRef.current.lastTriggerTime;
      const distancePx = distance; // already computed in px because we scaled by viewbox
      if (
        laneChanged ||
    (elapsed > TIME_THRESHOLD_S && distancePx > DISTANCE_THRESHOLD_PX)
      ) {
        const velocityHint = clamp((speed / 1600) + (point.pressure ?? 0) * 0.25, 0, 0.45);
        triggerNote(point, velocityHint, pointerStateRef.current.strokeId);
        pointerStateRef.current.lastTriggerTime = point.time;
        pointerStateRef.current.lastLane = lane;
      }

      pointerStateRef.current.lastPoint = point;
    },
    [ensureAudio, triggerNote],
  );

  const finalizeStroke = useCallback(() => {
    if (!liveStroke || !activeLayer) {
      setLiveStroke(null);
      return;
    }

    const eventsToAdd = pendingEventsRef.current;
    pendingEventsRef.current = [];

    setLayers((previous) =>
      previous.map((layer) => {
        if (layer.id !== activeLayer.id) {
          return layer;
        }

        const totalEvents = layer.events.length + eventsToAdd.length;
        if (totalEvents > MAX_EVENTS_PER_LAYER) {
          setLayerError(t('errors.layer_capacity'));
          return {
            ...layer,
            densityReached: true,
          };
        }

        setLayerError('');
        return {
          ...layer,
          densityReached: totalEvents >= MAX_EVENTS_PER_LAYER,
          strokes: [...layer.strokes, { ...liveStroke, points: [...liveStroke.points] }],
          events: loopEnabled ? [...layer.events, ...eventsToAdd] : layer.events,
        };
      }),
    );

    setLiveStroke(null);
    pointerStateRef.current = { drawing: false };
  }, [activeLayer, liveStroke, loopEnabled]);

  const handlePointerUp = useCallback(
    (event) => {
      if (pointerStateRef.current.drawing && event.pointerId === pointerStateRef.current.pointerId) {
        finalizeStroke();
      }
    },
    [finalizeStroke],
  );

  const handlePointerLeave = useCallback(() => {
    if (pointerStateRef.current.drawing) {
      finalizeStroke();
    }
  }, [finalizeStroke]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) {
      return undefined;
    }

    svg.addEventListener('pointerdown', handlePointerDown);
    svg.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    svg.addEventListener('pointerleave', handlePointerLeave);

    return () => {
      svg.removeEventListener('pointerdown', handlePointerDown);
      svg.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      svg.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp, handlePointerLeave]);

  const removeStrokeFromLayer = useCallback((layerId, strokeId) => {
    setLayers((previous) =>
      previous.map((layer) => {
        if (layer.id !== layerId) {
          return layer;
        }

        const remainingEvents = layer.events.filter((event) => event.strokeId !== strokeId);
        return {
          ...layer,
          strokes: layer.strokes.filter((stroke) => stroke.id !== strokeId),
          events: remainingEvents,
          densityReached: remainingEvents.length >= MAX_EVENTS_PER_LAYER,
        };
      }),
    );
  }, []);

  const undoLastStroke = useCallback(() => {
    const layer = layersRef.current.find((entry) => entry.id === activeLayerId);
    if (!layer || !layer.strokes.length) {
      return;
    }

    const lastStroke = layer.strokes[layer.strokes.length - 1];
    removeStrokeFromLayer(layer.id, lastStroke.id);
  }, [activeLayerId, layersRef, removeStrokeFromLayer]);

  const clearLayer = useCallback((layerId) => {
    setLayerError('');
    setLayers((previous) =>
      previous.map((layer) => (layer.id === layerId ? { ...layer, strokes: [], events: [], densityReached: false } : layer)),
    );
  }, []);

  const resetAll = useCallback(() => {
    setLayers([createEmptyLayer(0)]);
    setActiveLayerId('layer-1');
    setLiveStroke(null);
    setRecentNotes([]);
    setLayerError('');
    setIsPlaying(false);
    pointerStateRef.current = { drawing: false };
    loopStartRef.current = null;
    const ctx = audioContextRef.current;
    activeVoicesRef.current.forEach((voice) => {
      if (voice.cleanupTimeout) {
        window.clearTimeout(voice.cleanupTimeout);
      }
      const gateNode = voice?.gate;
      if (gateNode && ctx) {
        try {
          gateNode.gain.cancelScheduledValues(ctx.currentTime);
          gateNode.gain.setValueAtTime(0, ctx.currentTime);
        } catch (error) {
          // ignore envelope cancellation issues
        }
      }
      if (gateNode) {
        try {
          gateNode.disconnect();
        } catch (disconnectError) {
          // ignore if already disconnected
        }
      }
    });
    activeVoicesRef.current.clear();
  }, []);

  const addLayer = useCallback(() => {
    setLayers((previous) => {
      if (previous.length >= MAX_LAYERS) {
        setLayerError(t('errors.max_layers'));
        return previous;
      }

      const nextIndex = previous.length;
      const newLayer = createEmptyLayer(nextIndex);
      setLayerError('');
      return [...previous, newLayer];
    });
  }, []);

  const toggleMute = useCallback((layerId) => {
    setLayers((previous) =>
      previous.map((layer) =>
        layer.id === layerId
          ? { ...layer, muted: !layer.muted, solo: layer.solo && layer.muted ? false : layer.solo }
          : layer,
      ),
    );
  }, []);

  const toggleSolo = useCallback((layerId) => {
    setLayers((previous) =>
      previous.map((layer) => (layer.id === layerId ? { ...layer, solo: !layer.solo } : { ...layer, solo: false })),
    );
  }, []);

  const startTransport = useCallback(() => {
    const ctx = ensureAudio();
    if (!ctx) {
      return;
    }

    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    setIsPlaying((prev) => {
      if (prev) {
        return prev;
      }

      const startOffset = 0.06;
      loopStartRef.current = ctx.currentTime + startOffset;
      layersRef.current.forEach((layer) => {
        layer.events.forEach((ev) => {
          delete ev.__scheduledAt;
        });
      });

      return true;
    });
  }, [ensureAudio, layersRef]);

  const stopTransport = useCallback(() => {
    setIsPlaying((prev) => {
      if (!prev) {
        return prev;
      }
      return false;
    });
  }, []);

  const captureLoopAudio = useCallback(async () => {
    if (typeof window === 'undefined' || !window.MediaRecorder) {
      return null;
    }

    const destination = mediaDestRef.current;
    const context = ensureAudio();
    if (!destination || !context) {
      return null;
    }

    const stream = destination.stream;
    if (!stream) {
      return null;
    }

    const mimeCandidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm'];
    const supportedMime = mimeCandidates.find((type) => {
      try {
        return window.MediaRecorder.isTypeSupported(type);
      } catch (error) {
        return type === 'audio/webm';
      }
    });

    let recorder;
    try {
      recorder = new window.MediaRecorder(stream, supportedMime ? { mimeType: supportedMime } : undefined);
    } catch (error) {
      console.error('Failed to initialise audio recorder', error);
      return null;
    }

    const wasPlaying = isPlayingRef.current;
    if (!wasPlaying) {
      startTransport();
    }

    const chunks = [];
    const secondsPerBeat = 60 / tempoRef.current;
    const loopDurationMs = Math.max(600, LOOP_LENGTH_BEATS * secondsPerBeat * 1000 + 120);

    return new Promise((resolve) => {
      const handleStop = () => {
        recorder.removeEventListener('stop', handleStop);
        recorder.removeEventListener('error', handleError);

        if (!wasPlaying) {
          stopTransport();
        }

        if (!chunks.length) {
          resolve(null);
          return;
        }

        const mimeType = recorder.mimeType || supportedMime || 'audio/webm';
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        resolve({ blob, url, mimeType });
      };

      const handleError = (event) => {
        console.error('Audio recording failed', event?.error || event);
        recorder.removeEventListener('stop', handleStop);
        recorder.removeEventListener('error', handleError);
        if (recorder.state !== 'inactive') {
          try {
            recorder.stop();
          } catch (stopError) {
            // ignore
          }
        }
        if (!wasPlaying) {
          stopTransport();
        }
        resolve(null);
      };

      recorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size) {
          chunks.push(event.data);
        }
      });
      recorder.addEventListener('stop', handleStop);
      recorder.addEventListener('error', handleError);

      try {
        recorder.start();
      } catch (startError) {
        console.error('Failed to start audio recorder', startError);
        handleError(startError);
        return;
      }

      window.setTimeout(() => {
        if (recorder.state === 'recording') {
          try {
            recorder.stop();
          } catch (stopError) {
            handleError(stopError);
          }
        }
      }, loopDurationMs);
    });
  }, [ensureAudio, isPlayingRef, startTransport, stopTransport, tempoRef]);

  const saveImage = useCallback(async () => {
    if (isSavingRef.current) {
      return;
    }

    if (!svgRef.current) {
      return;
    }

    setIsSavingPiece(true);

    const renderToPng = () =>
      new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = VIEWBOX_WIDTH;
        canvas.height = VIEWBOX_HEIGHT;
        const context = canvas.getContext('2d');

        if (!context) {
          reject(new Error('Canvas is unavailable'));
          return;
        }

        try {
          renderChromaticComposition({
            context,
            layers: layersRef.current,
            liveStroke: liveStrokeRef.current,
            fallbackBrush: brushRef.current,
          });
        } catch (error) {
          reject(error);
          return;
        }

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to encode PNG'));
            return;
          }

          resolve({
            blob,
            url: URL.createObjectURL(blob),
          });
        }, 'image/png');
      });

    let imageResult;
    let audioCapture;

    try {
      [imageResult, audioCapture] = await Promise.all([renderToPng(), captureLoopAudio()]);
      const timestamp = new Date().toISOString();
      const piece = {
        id: uuidv4(),
        imageUrl: imageResult.url,
        imageMimeType: 'image/png',
        audioUrl: audioCapture?.url ?? null,
        audioMimeType: audioCapture?.mimeType ?? null,
        timestamp,
        tempo: tempoRef.current,
        layerCount: layersRef.current.length,
      };

      setSavedPieces((prev) => {
        const next = [piece, ...prev];
        return next.slice(0, SAVE_LIMIT);
      });
      showSaveToast(t('toasts.saved'));
    } catch (error) {
      if (imageResult?.url && imageResult.url.startsWith('blob:')) {
        URL.revokeObjectURL(imageResult.url);
      }
      console.error('Failed to save chromatic music piece', error);
      showSaveToast(t('errors.unable_to_save'));
    } finally {
      setIsSavingPiece(false);
    }
  }, [brushRef, captureLoopAudio, isSavingRef, layersRef, liveStrokeRef, showSaveToast, svgRef, tempoRef]);

  const downloadPiece = useCallback((piece) => {
    if (!piece?.imageUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = piece.imageUrl;
    const timestamp = piece.timestamp ? piece.timestamp.replace(/[:T]/g, '-').split('.')[0] : '';
    link.download = timestamp ? `chromatic-music-${timestamp}.png` : 'chromatic-music.png';
    link.click();
  }, []);

  useEffect(
    () => () => {
      savedPiecesRef.current.forEach((piece) => {
        if (piece.audioUrl && piece.audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(piece.audioUrl);
        }
        if (piece.imageUrl && piece.imageUrl.startsWith('blob:')) {
          URL.revokeObjectURL(piece.imageUrl);
        }
      });
    },
    [savedPiecesRef],
  );

  useEffect(
    () => () => {
      timeoutsRef.current.forEach((timeout) => window.clearTimeout(timeout));
      const ctx = audioContextRef.current;
      activeVoicesRef.current.forEach((voice) => {
        if (voice.cleanupTimeout) {
          window.clearTimeout(voice.cleanupTimeout);
        }
        const gateNode = voice?.gate;
        if (!gateNode) {
          return;
        }
        if (ctx) {
          try {
            gateNode.gain.cancelScheduledValues(ctx.currentTime);
            gateNode.gain.setValueAtTime(0, ctx.currentTime);
          } catch (error) {
            // ignore
          }
        }
        try {
          gateNode.disconnect();
        } catch (disconnectError) {
          // already disconnected
        }
      });
      activeVoicesRef.current.clear();
    },
    [],
  );

  useEffect(() => {
    if (!audioReady || !audioContextRef.current) {
      return undefined;
    }

    let rafId;
    const tick = () => {
      const context = audioContextRef.current;
      const loopStart = loopStartRef.current;
      const tempoValue = tempoRef.current;

      if (loopStart && isPlayingRef.current) {
        const secondsPerBeat = 60 / tempoValue;
        const loopLengthSeconds = LOOP_LENGTH_BEATS * secondsPerBeat;
        const now = context.currentTime;
        const elapsed = now - loopStart;
        const cycle = Math.floor(elapsed / loopLengthSeconds);
        const positionInCycle = elapsed - cycle * loopLengthSeconds;

        const lookAhead = 0.18;
        const EPS = 0.01; // 10 ms grace so near-now events aren’t missed

        const scheduleForCycle = (cycleOffset) => {
          const cycleStartTime = loopStart + (cycle + cycleOffset) * loopLengthSeconds;
          layersRef.current.forEach((layer) => {
            const isAudible = (() => {
              const soloed = layersRef.current.filter((entry) => entry.solo);
              if (soloed.length) {
                return layer.solo;
              }
              return !layer.muted;
            })();
            if (!isAudible) {
              return;
            }
            layer.events.forEach((event) => {
              if (!event.__scheduledAt) {
                event.__scheduledAt = -1;
              }
              const mode = playbackModeRef.current;
              const beatPos =
                mode === 'scan-lr' ? (event.x ?? 0) * LOOP_LENGTH_BEATS :
                  mode === 'scan-rl' ? (1 - (event.x ?? 0)) * LOOP_LENGTH_BEATS :
                    event.time; // gesture (default)
              const eventTime = cycleStartTime + beatPos * secondsPerBeat;
              if (eventTime >= now - EPS && eventTime < now + lookAhead && event.__scheduledAt !== eventTime) {
                event.__scheduledAt = eventTime;
                // play exactly what we recorded (same time, same velocity)
                scheduleNote(event, eventTime, layer.id);
              }
            });
          });
        };

        scheduleForCycle(0);
        if (positionInCycle + lookAhead >= loopLengthSeconds - 0.05) {
          scheduleForCycle(1);
        }

        const beatPosition = elapsed / secondsPerBeat;
        const nextBeat = Math.floor(beatPosition) % BEATS_PER_BAR;
        setMetronomeBeat((previous) => (previous === nextBeat ? previous : nextBeat));
        const phase = beatPosition % 1;
        setMetronomePhase((previous) => (Math.abs(previous - phase) > 0.02 ? phase : previous));
      }

      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [audioReady, layersRef, isPlayingRef, scheduleNote, tempoRef]);

  useEffect(() => {
    if (canvasActive && !audioContextRef.current) {
      const context = ensureAudio();
      if (context.state === 'suspended') {
        context.resume();
      }
      setAudioReady(true);
    }
  }, [canvasActive, ensureAudio]);

  const handleTempoChange = useCallback((event) => {
    const value = Number(event.target.value);
    setTempo(value);
  }, []);

  const toggleLoop = useCallback(() => {
    setLoopEnabled((previous) => {
      if (previous) {
        return false;
      }
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      return true;
    });
  }, []);

  // New: Play/Stop transport
  const togglePlay = useCallback(() => {
    if (isPlayingRef.current) {
      stopTransport();
    } else {
      startTransport();
    }
  }, [isPlayingRef, startTransport, stopTransport]);

  useEffect(() => {
    // If we're playing, force reschedule so the new mode takes effect immediately
    if (!isPlaying) return;
    layersRef.current.forEach((layer) => {
      layer.events.forEach((ev) => {
        delete ev.__scheduledAt;
      });
    });
  }, [isPlaying, playbackMode, layersRef]);

  const visibleLayers = useMemo(
    () =>
      layers.map((layer) => ({
        ...layer,
        color: layer.strokes[0]?.brushId
          ? (INSTRUMENTS.find((entry) => entry.id === layer.strokes[0].brushId)?.color ?? brush.color)
          : brush.color,
      })),
    [brush.color, layers],
  );

  return {
    svgRef,
    brush,
    brushId,
    setBrushId,
    scale,
    scaleId,
    setScaleId,
    tempo,
    handleTempoChange,
    loopEnabled,
    isPlaying,
    togglePlay,
    toggleLoop,
    playbackMode,
    setPlaybackMode,
    echoEnabled,
    setEchoEnabled,
    quietMode,
    setQuietMode,
    masterVolume,
    setMasterVolume,
    layers,
    visibleLayers,
    MAX_LAYERS,
    activeLayer,
    activeLayerId,
    setActiveLayerId,
    liveStroke,
    recentNotes,
    undoLastStroke,
    clearLayer,
    resetAll,
    saveImage,
    isSaving: isSavingPiece,
    savedPieces,
    saveToast,
    downloadPiece,
    toggleMute,
    toggleSolo,
    addLayer,
    layerError,
    metronomeBeat,
    metronomePhase,
    // expose a helper to fetch the audio MediaStream
    getAudioStream: () => {
      // ensure graph exists before we try to read its MediaStream
      ensureAudio();
      return mediaDestRef.current?.stream ?? null;
    },
  };
};

export default useChromaticMusicEngine;
